import { Autocomplete, AutocompleteValue, CircularProgress, TextField } from '@mui/material';
import { SyntheticEvent, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ServerAutocompleteProps } from 'shared/components/form/ServerSearchAutocomplete/ServerSearchAutocomplete';
import { useServerSearch } from 'shared/components/form/ServerSearchAutocomplete/useServerSearch';

import { useLocationAutocompleteSearchOptions } from '../../hooks';
import { LocationKey, SearchLocationAutocompleteOption } from '../../types';
import SearchLocationAutocompleteOptionRow from './SearchLocationAutocompleteOptionRow';

export interface PseudoEvent<T> {
    target: { name: string; value: T };
}

export const SEARCH_LOCATION_AUTOCOMPLETE_MAX_LENGTH = 100;

export type SearchLocationAutocompleteProps<
Multiple extends boolean = false
> = Pick<
    ServerAutocompleteProps<LocationKey, Multiple>,
    'disabled' |
    'placeholder' |
    'helperText' |
    'label' |
    'required' |
    'onChange' |
    'value' |
    'multiple' |
    'inputRef' |
    'size' |
    'error'
> & { id?: string; autoFocus?: boolean };

export default function SearchLocationAutocomplete<
    Multiple extends boolean = false
>({
    onChange,
    multiple,
    value,
    label,
    placeholder,
    helperText,
    inputRef,
    size,
    required,
    ...props
}: SearchLocationAutocompleteProps<Multiple>) {
    const { t } = useTranslation('common');
    const { searchOptions, endSession } = useLocationAutocompleteSearchOptions();

    const [ inputValue, setInputValue ] = useState('');
    const { options, loading, error } = useServerSearch(inputValue, {
        fetcher: searchOptions
    });

    const handleChange = useCallback((_: SyntheticEvent<Element, Event>, value: AutocompleteValue<SearchLocationAutocompleteOption, Multiple, false, false>) => {
        endSession();
        onChange(value);
    }, [ onChange, endSession ]);

    const handleBlur = useCallback(() => {
        if (!inputValue) {
            return;
        }

        // If the selector supports multiple, only custom user input remains.
        // If the selector doesn't support multiple values, the custom user input remains unless it is equal to the selected value
        if (multiple || (!value || (value as unknown as SearchLocationAutocompleteOption).text !== inputValue)) {

            const autocompleteOption: SearchLocationAutocompleteOption = { text: inputValue };

            const newValue = (
                multiple
                    ? [ ...value as SearchLocationAutocompleteOption[], autocompleteOption ]
                    : autocompleteOption
            ) as AutocompleteValue<SearchLocationAutocompleteOption, Multiple, false, false>;

            onChange(newValue);
        }
    }, [ inputValue, multiple, onChange, value ]);

    return (
        <Autocomplete<SearchLocationAutocompleteOption, Multiple, false, false>
            { ...props }
            onChange={ handleChange }
            onBlur={ handleBlur }
            onInputChange={ (_, v) => setInputValue(v) }
            getOptionLabel={ x => x.text }
            renderOption={ (props, option) => (
                <SearchLocationAutocompleteOptionRow { ...props }
                    option={ option }
                    elementProps={ props }
                />
            ) }
            isOptionEqualToValue={ () => false }
            options={ options }
            value={ value }
            multiple={ multiple }
            loading={ loading }
            limitTags={ 3 }
            disableCloseOnSelect={ false }
            forcePopupIcon={ false }
            handleHomeEndKeys={ false }
            selectOnFocus
            filterOptions={ (options) => options }
            renderInput={ (params) =>
                (
                    <TextField
                        { ...params }
                        label={ label }
                        placeholder={ placeholder }
                        size={ size }
                        error={ !!error }
                        helperText={ error ? t('inputs.autocomplete.fetchError') : helperText }
                        inputRef={ inputRef }
                        required={ required }
                        slotProps={{
                            input: {
                                ...params.InputProps,
                                inputProps: {
                                    ...params.inputProps,
                                    maxLength: SEARCH_LOCATION_AUTOCOMPLETE_MAX_LENGTH
                                },
                                endAdornment: (
                                    <>
                                        { loading ? <CircularProgress size={ 18 } /> : null }
                                        { params.InputProps.endAdornment }
                                    </>
                                )
                            } }}
                    />
                )
            }
        />
    );
}
